import { useState, useRef, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  ButtonGroup,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DashboardIcon from '@material-ui/icons/Dashboard';
import RefreshIcon from '@material-ui/icons/Refresh';

// ---------------------------------------------------------------------------
// Dashboard registry
// Each environment has a Grafana subdomain: grafana.{env}.exoworks.inc
// Add dashboard IDs here as you discover them per env.
// ---------------------------------------------------------------------------

interface DashboardDef {
  id: string;
  slug: string;
  label: string;
}

const GRAFANA_DASHBOARDS: Record<string, DashboardDef[]> = {
  qa: [
    { id: 'adr4sjldxgqo0d', slug: 'qa-us-essential', label: 'Essential (EW)' },
    { id: 'adr4svwrk4ttsd', slug: 'qa-us-pulse',     label: 'Pulse' },
  ],
  dev: [
    // TODO: replace with actual dev dashboard IDs
    { id: 'edr4rxnczkm4gc', slug: 'dev-us-essential', label: 'Essential (EW)' },
    { id: 'edr4s3j2drmkge', slug: 'dev-us-pulse',     label: 'Pulse' },
  ],
  uat: [
    { id: 'af4ofboteyr5sf', slug: 'uat-us-essential', label: 'Essential (EW)' },
    { id: 'bf4og26yjoykga', slug: 'uat-us-pulse',     label: 'Pulse' },
  ],
  sandbox: [
    // TODO: replace with actual sandbox dashboard IDs
    { id: 'ff4ol3y078kcgc', slug: 'sandbox-us-essential', label: 'Essential (EW)' },
    { id: 'ff4olehiov5z4e', slug: 'sandbox-us-pulse',     label: 'Pulse' },
  ],
};

const ENVIRONMENTS = ['qa', 'uat', 'dev', 'sandbox'];

const grafanaUrl = (env: string, dashboard: DashboardDef) =>
  `https://grafana.qa.exoworks.inc/d/${dashboard.id}/${dashboard.slug}?orgId=1&theme=dark`;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  root: { height: '100%', display: 'flex', flexDirection: 'column' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  selectorLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginRight: theme.spacing(0.5),
  },
  envBtn: {
    textTransform: 'none' as const,
    minWidth: 80,
  },
  serviceBtn: {
    textTransform: 'none' as const,
    minWidth: 120,
  },
  spacer: { flex: 1 },
  openBtn: { textTransform: 'none' as const, whiteSpace: 'nowrap' as const },
  refreshBtn: { minWidth: 'auto' },
  iframeWrapper: {
    flex: 1,
    position: 'relative' as const,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: '#0b1117',
    minHeight: 600,
  },
  iframe: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  loaderOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#0b1117',
    zIndex: 1,
    color: theme.palette.text.secondary,
  },
  embeddingNote: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  noteText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GrafanaPage = () => {
  const classes = useStyles();
  const [env, setEnv] = useState('qa');
  const [dashboardIdx, setDashboardIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0); // increment to force reload
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const dashboards = GRAFANA_DASHBOARDS[env] ?? [];
  const dashboard = dashboards[dashboardIdx] ?? dashboards[0];
  const url = dashboard ? grafanaUrl(env, dashboard) : '';

  // Reset dashboard index when env changes (in case counts differ)
  useEffect(() => {
    setDashboardIdx(0);
    setLoading(true);
  }, [env]);

  useEffect(() => {
    setLoading(true);
  }, [dashboardIdx, iframeKey]);

  const handleRefresh = () => {
    setIframeKey(k => k + 1);
  };

  return (
    <Box className={classes.root}>
      {/* ── Toolbar ── */}
      <Box className={classes.toolbar}>
        {/* Environment selector */}
        <Typography className={classes.selectorLabel}>Environment</Typography>
        <ButtonGroup size="small" variant="outlined">
          {ENVIRONMENTS.map(e => (
            <Button
              key={e}
              className={classes.envBtn}
              variant={env === e ? 'contained' : 'outlined'}
              color={env === e ? 'primary' : 'default'}
              onClick={() => setEnv(e)}
            >
              {e.toUpperCase()}
            </Button>
          ))}
        </ButtonGroup>

        {/* Dashboard (service) selector */}
        <Typography className={classes.selectorLabel} style={{ marginLeft: 8 }}>
          Dashboard
        </Typography>
        <ButtonGroup size="small" variant="outlined">
          {dashboards.map((d, i) => (
            <Button
              key={d.id + d.label}
              className={classes.serviceBtn}
              variant={dashboardIdx === i ? 'contained' : 'outlined'}
              color={dashboardIdx === i ? 'primary' : 'default'}
              onClick={() => setDashboardIdx(i)}
            >
              {d.label}
            </Button>
          ))}
        </ButtonGroup>

        <Box className={classes.spacer} />

        {/* Refresh */}
        <Button
          size="small"
          variant="outlined"
          className={classes.refreshBtn}
          onClick={handleRefresh}
          title="Reload dashboard"
        >
          <RefreshIcon fontSize="small" />
        </Button>

        {/* Open in Grafana */}
        {url && (
          <Button
            size="small"
            variant="outlined"
            className={classes.openBtn}
            startIcon={<OpenInNewIcon />}
            href={url.replace('&kiosk', '')} // open without kiosk so the user sees full Grafana
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Grafana
          </Button>
        )}
      </Box>

      {/* ── Iframe ── */}
      {url ? (
        <>
          <Box className={classes.iframeWrapper}>
            {loading && (
              <Box className={classes.loaderOverlay}>
                <CircularProgress size={32} />
                <Typography variant="body2">Loading dashboard…</Typography>
                <Typography variant="caption" style={{ opacity: 0.6, textAlign: 'center', maxWidth: 320 }}>
                  If prompted to log in, sign into Grafana in a separate tab first, then return here.
                </Typography>
              </Box>
            )}
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={url}
              className={classes.iframe}
              title={`Grafana — ${dashboard?.label}`}
              allow="fullscreen"
              onLoad={() => setLoading(false)}
            />
          </Box>

          {/* Note about embedding requirements */}
          <Box className={classes.embeddingNote} mt={1}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <DashboardIcon style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', display: 'block' }} />
              </Grid>
              <Grid item xs>
                <Typography className={classes.noteText}>
                  Dashboards embed in kiosk mode. If you see a blank panel or login screen,{' '}
                  <a href={`https://grafana.qa.exoworks.inc`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                    open Grafana
                  </a>{' '}
                  in a new tab to authenticate with Okta, then come back and refresh.
                  Grafana must have <code>allow_embedding = true</code> set in its config.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={300}>
          <Typography color="textSecondary">No dashboards configured for {env}.</Typography>
        </Box>
      )}
    </Box>
  );
};
